import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { getGuideDispatchs } from '../services/DispatchServices'
import { DispatchGuideDto } from '../interfaces'
import { guideKey, hasPieces } from '../utils/buildDispatchPayload'
import { mergeSelection, toggleSelection } from '../utils/shipmentSelection'
import { GuideDispatchField } from '../components/Dispatch/GuidesDispatchTables'

/**
 * Guías pendientes de despacho: traer, filtrar, ordenar, paginar y seleccionar.
 *
 * Vive fuera del componente porque es la zona donde aparecieron los cuatro bugs
 * de selección (comparar por referencia mientras se pinta por valor, "todo
 * seleccionado" por longitud, "seleccionar todo" que reemplaza en vez de sumar,
 * y O(n×m) al pintar). Con la frontera puesta, ninguno se puede volver a colar
 * desde el JSX.
 */

/** Fila con lo que hace falta precalculado. Ver `searchBlob`. */
interface GuideRow {
  guide: DispatchGuideDto
  key: string
  /**
   * Todo lo buscable de la fila, junto y en minúsculas, calculado UNA vez al
   * recibir la lista.
   *
   * Antes el filtro corría `moment(...).format('DD/MM/YYYY')` por fila y por
   * token en CADA tecla. Con 5000 guías eso son 5000 construcciones de moment
   * por pulsación: el buscador se arrastra. Precalcular lo deja en un
   * `includes` sobre un string.
   */
  searchBlob: string
}

const toRow = (guide: DispatchGuideDto): GuideRow => ({
  guide,
  key: guideKey(guide),
  searchBlob: [
    guide.numGuia,
    guide.origen,
    guide.ciudadDestino,
    guide.fechaCreacion ? moment(guide.fechaCreacion).format('DD/MM/YYYY') : '',
  ]
    .map((v) => (v ?? '').toString().toLowerCase())
    .join(' '),
})

export interface PreselectSummary {
  /** Guías de la carga masiva que se pidió preseleccionar (M). */
  requested: number
  /** Cuántas quedaron efectivamente tildadas al abrir (N inicial). */
  matched: number
  /**
   * Cuántas de las pedidas NO se pueden despachar (D): no vuelven en
   * `GetAllMissingCCW` (ya manifestadas, anuladas, de otro cliente) o no
   * declaran piezas.
   *
   * Se calcula UNA vez, en la misma carga que siembra la preselección, y se
   * congela. Recalcularlo contra un refetch con fechas acotadas lo inflaría y el
   * aviso diría que hay guías "ya manifestadas o anuladas" cuando solo están
   * fuera del filtro.
   */
  unavailable: number
  /**
   * Las que NO aparecieron en la lista, por número.
   *
   * `unavailable` mezcla dos causas muy distintas y por eso no alcanza para
   * explicarle nada al operador. Estas son las que hay que ir a buscar contra
   * `POST /manifest/by-shipment` para decir en qué manifiesto quedaron.
   */
  notInList: string[]
  /**
   * Las que SÍ están en la lista pero declaran 0 piezas, por número.
   *
   * No se preseleccionan (producirían `physicalWeight: Infinity`), pero el
   * operador las puede tildar a mano. Consultarlas contra manifiestos no
   * aportaría nada: el problema es el dato de la guía, no dónde está.
   */
  withoutPieces: string[]
}

export interface UseDispatchGuidesArgs {
  accountCode?: string | null
  accountId?: string | null
  /** Números de guía a tildar tras la primera carga. Vacío = flujo manual. */
  preselectedGuides?: string[]
}

export function useDispatchGuides({
  accountCode,
  accountId,
  preselectedGuides,
}: UseDispatchGuidesArgs) {
  const [rows, setRows] = useState<GuideRow[]>([])
  const [selecteds, setSelecteds] = useState<DispatchGuideDto[]>([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [registersNumber, setRegistersNumber] = useState(10)
  const [minDate, setMinDate] = useState<Date | null>(null)
  const [maxDate, setMaxDate] = useState<Date | null>(null)
  const [sortBy, setSortBy] = useState({
    field: GuideDispatchField.DATECREATE,
    asc: false,
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Distingue "todavía no cargó" de "cargó y vino vacía". */
  const [loaded, setLoaded] = useState(false)

  const [preselectSummary, setPreselectSummary] =
    useState<PreselectSummary | null>(null)
  const didPreselect = useRef(false)

  const fetchGuides = useCallback(async () => {
    if (!accountCode || !accountId) return

    setLoading(true)
    const res = await getGuideDispatchs(
      '' + accountCode,
      '' + accountId,
      minDate ? moment(minDate).format('YYYY-MM-DD') : undefined,
      maxDate ? moment(maxDate).format('YYYY-MM-DD') : undefined
    )
    setLoading(false)

    if (res?.didError) {
      // Un error NO puede terminar en una tabla vacía: el estado vacío dice
      // "aún no tienes preguías" y eso sería mentirle a alguien que acaba de
      // cargar 1500.
      setError(res.errorMessage || 'No se pudieron consultar las guías.')
      return
    }

    setError(null)
    setRows((Array.isArray(res?.model) ? res!.model! : []).map(toRow))
    setLoaded(true)
  }, [accountCode, accountId, minDate, maxDate])

  useEffect(() => {
    fetchGuides()
    // Solo en el montaje y al cambiar de cliente: las fechas se aplican con el
    // botón Buscar, no al tipear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode, accountId])

  // --- Preselección, una sola vez tras la primera carga con éxito ---
  useEffect(() => {
    if (didPreselect.current || !loaded) return
    const wanted = preselectedGuides ?? []
    if (!wanted.length) return
    didPreselect.current = true

    const wantedKeys = new Set(
      wanted.map((n) => n.trim().toUpperCase()).filter(Boolean)
    )
    const found = rows.filter((r) => wantedKeys.has(r.key))
    const usable = found.filter((r) => hasPieces(r.guide))

    const foundKeys = new Set(found.map((r) => r.key))
    const usableKeys = new Set(usable.map((r) => r.key))

    setSelecteds(usable.map((r) => r.guide))
    setPreselectSummary({
      requested: wantedKeys.size,
      matched: usable.length,
      unavailable: wantedKeys.size - usable.length,
      // Se guardan los NÚMEROS tal como los pidió el llamador (no las claves
      // normalizadas): son los que se le muestran al operador y los que viajan al
      // endpoint de manifiestos, que devuelve la guía exactamente como se envió.
      notInList: wanted.filter((n) => {
        const k = n.trim().toUpperCase()
        return !!k && !foundKeys.has(k)
      }),
      withoutPieces: wanted.filter((n) => {
        const k = n.trim().toUpperCase()
        return !!k && foundKeys.has(k) && !usableKeys.has(k)
      }),
    })
  }, [loaded, rows, preselectedGuides])

  /**
   * Vuelve a tildar las guías de la carga, para cuando el operador limpió la
   * selección y quiere recuperarla sin cerrar el modal ni buscarlas una por una.
   *
   * MERGE, no reemplazo: las guías sueltas que haya sumado a mano se respetan.
   * Trabaja sobre `rows` (la lista viva), así que si el operador cambió los filtros
   * recupera las que estén visibles ahora.
   */
  const restorePreselection = useCallback(() => {
    const wanted = preselectedGuides ?? []
    if (!wanted.length) return
    const wantedKeys = new Set(
      wanted.map((n) => n.trim().toUpperCase()).filter(Boolean)
    )
    const usable = rows
      .filter((r) => wantedKeys.has(r.key) && hasPieces(r.guide))
      .map((r) => r.guide)
    setSelecteds((prev) => mergeSelection(prev, usable, true, guideKey))
  }, [rows, preselectedGuides])

  // --- Derivados ---
  const filteredRows = useMemo(() => {
    const tokens = search.toLowerCase().split(' ').filter(Boolean)
    if (!tokens.length) return rows
    return rows.filter((r) => tokens.every((t) => r.searchBlob.includes(t)))
  }, [rows, search])

  const sortedRows = useMemo(() => {
    // Copia antes de ordenar: `filteredRows` puede ser el mismo array que `rows`
    // cuando no hay búsqueda, y ordenarlo in place mutaría el estado.
    const copy = [...filteredRows]
    const dir = sortBy.asc ? 1 : -1
    copy.sort((ra, rb) => {
      const a = ra.guide
      const b = rb.guide
      switch (sortBy.field) {
        case GuideDispatchField.NUM_GUIDE:
          return dir * (a.numGuia ?? '').localeCompare(b.numGuia ?? '')
        case GuideDispatchField.ORIGEN:
          return dir * (a.origen ?? '').localeCompare(b.origen ?? '')
        case GuideDispatchField.DATECREATE:
          return (
            dir * (a.fechaCreacion ?? '').localeCompare(b.fechaCreacion ?? '')
          )
        case GuideDispatchField.SALE:
          return dir * (a.sealNumber ?? '').localeCompare(b.sealNumber ?? '')
        case GuideDispatchField.PESO:
          return dir * ((a.peso ?? 0) - (b.peso ?? 0))
        default:
          return 0
      }
    })
    return copy
  }, [filteredRows, sortBy])

  const filteredGuides = useMemo(
    () => filteredRows.map((r) => r.guide),
    [filteredRows]
  )

  const paginatedGuides = useMemo(() => {
    const start = page * registersNumber
    return sortedRows.slice(start, start + registersNumber).map((r) => r.guide)
  }, [sortedRows, page, registersNumber])

  const selectedKeys = useMemo(
    () => new Set(selecteds.map(guideKey)),
    [selecteds]
  )

  /** Claves de las guías que pertenecen a la carga masiva de origen. */
  const bulkKeys = useMemo(
    () =>
      new Set(
        (preselectedGuides ?? [])
          .map((n) => n.trim().toUpperCase())
          .filter(Boolean)
      ),
    [preselectedGuides]
  )

  /**
   * Cuántas de las guías DE LA CARGA están tildadas ahora.
   *
   * No es `selecteds.length`: el operador puede sumar guías sueltas para
   * aprovechar el manifiesto. Mezclar los dos números produce cosas como
   * "7 de 1 guías", que además de fea es falsa — son universos distintos.
   */
  const selectedFromBulk = useMemo(
    () => selecteds.filter((s) => bulkKeys.has(guideKey(s))).length,
    [selecteds, bulkKeys]
  )

  /** Guías tildadas que NO vienen de la carga: las que el operador sumó. */
  const selectedExtra = selecteds.length - selectedFromBulk

  const allFilteredSelected = useMemo(
    () =>
      filteredRows.length > 0 && filteredRows.every((r) => selectedKeys.has(r.key)),
    [filteredRows, selectedKeys]
  )

  /**
   * Al abrir con preselección, saltar a la página donde está la primera guía
   * tildada.
   *
   * `GetAllMissingCCW` devuelve TODO el backlog del cliente ordenado por fecha
   * descendente. Si la carga es de hace unos días y desde entonces se crearon
   * cientos de guías, las preseleccionadas caen en la página 6, 8 o 12: el
   * operador abría el modal, veía la primera página sin un solo check, y tenía
   * que ir a buscarlas a mano.
   *
   * Una sola vez. Después el operador navega solo y no se le mueve el piso.
   */
  const didJumpToSelection = useRef(false)
  useEffect(() => {
    if (didJumpToSelection.current) return
    if (!preselectSummary) return // flujo manual: no hay a dónde saltar
    if (sortedRows.length === 0) return

    const idx = sortedRows.findIndex((r) => selectedKeys.has(r.key))
    didJumpToSelection.current = true
    if (idx >= 0) setPage(Math.floor(idx / registersNumber))
  }, [preselectSummary, sortedRows, selectedKeys, registersNumber])

  // --- Acciones ---
  const toggleGuide = useCallback((guide: DispatchGuideDto) => {
    setSelecteds((prev) => toggleSelection(prev, guide, guideKey))
  }, [])

  /**
   * Marca o desmarca un lote de golpe: rango con Shift, arrastre por la columna
   * del checkbox. Va por `mergeSelection`, así que no duplica lo ya marcado.
   */
  const setRangeSelected = useCallback(
    (guides: DispatchGuideDto[], selected: boolean) => {
      setSelecteds((prev) => mergeSelection(prev, guides, selected, guideKey))
    },
    []
  )

  const toggleAllFiltered = useCallback(() => {
    setSelecteds((prev) =>
      mergeSelection(prev, filteredGuides, !allFilteredSelected, guideKey)
    )
  }, [filteredGuides, allFilteredSelected])

  const changeSort = useCallback((field: GuideDispatchField) => {
    setSortBy((prev) =>
      prev.field === field ? { ...prev, asc: !prev.asc } : { field, asc: true }
    )
  }, [])

  const clearFilters = useCallback(() => {
    setSearch('')
    setSelecteds([])
    setPage(0)
    setMinDate(null)
    setMaxDate(null)
    // Con una preselección activa, limpiar filtros no puede dejar al operador
    // sin las guías de su carga: se vuelve a sembrar.
    if (preselectedGuides?.length) didPreselect.current = false
  }, [preselectedGuides])

  const clearSelection = useCallback(() => setSelecteds([]), [])

  /**
   * Cuántas de la carga están tildadas ahora contra cuántas se podían tildar.
   * Es lo que decide si se ofrece el botón de restaurar: sin esto habría que
   * mostrarlo siempre, incluso sin nada que restaurar.
   */
  const restorableCount = useMemo(() => {
    if (!preselectSummary) return 0
    return Math.max(0, preselectSummary.matched - selectedFromBulk)
  }, [preselectSummary, selectedFromBulk])

  return {
    // datos
    guides: paginatedGuides,
    filteredCount: filteredRows.length,
    selecteds,
    selectedKeys,
    allFilteredSelected,
    preselectSummary,
    selectedFromBulk,
    selectedExtra,
    // estado de carga
    loading,
    error,
    loaded,
    // filtros / orden / paginado
    search,
    setSearch,
    page,
    setPage,
    registersNumber,
    setRegistersNumber,
    minDate,
    setMinDate,
    maxDate,
    setMaxDate,
    sortBy,
    changeSort,
    // acciones
    refresh: fetchGuides,
    toggleGuide,
    setRangeSelected,
    toggleAllFiltered,
    clearFilters,
    clearSelection,
    restorePreselection,
    restorableCount,
  }
}
